import { request } from '@/api/client'
import type { SecurityRule } from '@/types'

/** 三个模块的规则列表，仅安全设置页使用 */
export interface AppSecurity {
  redirect_allowlist: SecurityRule[]
  ip_allowlist: SecurityRule[]
  h5_trusted_domains: SecurityRule[]
}

/** GET /developer/apps/:appId/security 的单条规则 */
export interface SecurityRuleDto {
  /** app_security_rule 行 id */
  id?: string
  /** 规整后的值 */
  value?: string
}

/** GET /developer/apps/:appId/security 的 data */
export interface AppSecurityDto {
  redirect_urls?: SecurityRuleDto[]
  /** 公网 IPv4；没有时为空数组 */
  ip_whitelist?: SecurityRuleDto[]
  h5_trusted_domains?: SecurityRuleDto[]
  /** ip_whitelist 非空时为 true */
  ip_whitelist_enabled?: boolean
}

function mapRules(items: SecurityRuleDto[] | undefined): SecurityRule[] {
  if (!items) return []
  const seen = new Set<string>()
  const rules: SecurityRule[] = []
  for (const item of items) {
    const value = item.value?.trim() ?? ''
    if (!value || seen.has(value)) continue
    seen.add(value)
    rules.push({ id: item.id?.trim() ?? '', value })
  }
  return rules
}

/** 三个模块都映射成带行 id 的列表；缺省或空数组都是未配置。 */
export function mapAppSecurity(
  dto: AppSecurityDto | null | undefined,
): AppSecurity {
  return {
    redirect_allowlist: mapRules(dto?.redirect_urls),
    ip_allowlist: mapRules(dto?.ip_whitelist),
    h5_trusted_domains: mapRules(dto?.h5_trusted_domains),
  }
}

/** GET /developer/apps/:appId/security */
export function getAppSecurity(appId: string) {
  return request<AppSecurityDto | null>(
    `/developer/apps/${appId}/security`,
  ).then(mapAppSecurity)
}

/** POST /developer/apps/:appId/security/rules 的 rule_type */
export type SecurityRuleType = 'redirect_url' | 'ip_whitelist' | 'h5_trusted_domain'

/** POST 返回的新行 */
export interface CreatedSecurityRuleDto {
  id?: string
  rule_type?: string
  /** 规整后的入库值 */
  value?: string
}

export interface CreatedSecurityRule {
  id: string
  ruleType: string
  value: string
}

export function mapCreatedSecurityRule(
  dto: CreatedSecurityRuleDto | null | undefined,
  fallbackValue: string,
): CreatedSecurityRule {
  return {
    id: dto?.id?.trim() ?? '',
    ruleType: dto?.rule_type?.trim() ?? '',
    value: dto?.value?.trim() || fallbackValue.trim(),
  }
}

/** POST /developer/apps/:appId/security/rules，一次添加一条。 */
export function addSecurityRule(
  appId: string,
  input: { ruleType: SecurityRuleType; value: string },
) {
  return request<CreatedSecurityRuleDto | null>(
    `/developer/apps/${appId}/security/rules`,
    {
      method: 'POST',
      body: {
        rule_type: input.ruleType,
        value: input.value,
      },
    },
  ).then((dto) => mapCreatedSecurityRule(dto, input.value))
}

/** PUT /developer/apps/:appId/security/rules/:ruleId，修改一条规则的值。 */
export function updateSecurityRule(appId: string, ruleId: string, value: string) {
  return request<CreatedSecurityRuleDto | null>(
    `/developer/apps/${appId}/security/rules/${ruleId}`,
    {
      method: 'PUT',
      body: { value },
    },
  ).then((dto) => mapCreatedSecurityRule(dto, value))
}

/** DELETE /developer/apps/:appId/security/rules/:ruleId */
export function deleteSecurityRule(appId: string, ruleId: string) {
  return request<null>(`/developer/apps/${appId}/security/rules/${ruleId}`, {
    method: 'DELETE',
  })
}

/** PUT /developer/apps/:appId/security/batch-replace 返回的覆盖结果 */
export interface ReplacedSecurityRulesDto {
  rule_type?: string
  list?: SecurityRuleDto[]
}

/** 用返回的 list 覆盖该模块；没有 list 时按提交的 values 占位。 */
export function mapReplacedSecurityRules(
  dto: ReplacedSecurityRulesDto | null | undefined,
  fallbackValues: string[],
): SecurityRule[] {
  if (dto && 'list' in dto) return mapRules(dto.list)
  return mapRules(fallbackValues.map((value) => ({ value })))
}

/** PUT /developer/apps/:appId/security/batch-replace，整表覆盖一个模块。 */
export function replaceSecurityRules(
  appId: string,
  input: { ruleType: SecurityRuleType; values: string[] },
) {
  return request<ReplacedSecurityRulesDto | null>(
    `/developer/apps/${appId}/security/batch-replace`,
    {
      method: 'PUT',
      body: {
        rule_type: input.ruleType,
        values: input.values,
      },
    },
  ).then((dto) => mapReplacedSecurityRules(dto, input.values))
}
